import './TelemetryPanel.css'
import { useApi } from '@/hooks/useApi'
import { useInterval } from '@/hooks/useInterval'
import { formatDecimal } from '@/utilities/formatters'
import { telemetryQuerySpec, type TelemetryRow } from 'aevocam-rest-api-spec'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from '@/components/graphics/Spinner'
import { TelemetrySparkline } from './TelemetrySparkline'
import {
  rowsToTelemetrySeries,
  TELEMETRY_PERIOD_SEC,
  TELEMETRY_REFRESH_MS,
  telemetryWindow,
  type TelemetrySeries,
} from './telemetryHelpers'

export function TelemetryPanel() {
  const api = useApi({
    requireLogin: true,
    adminOnly: true,
  })

  const [series, setSeries] = useState<TelemetrySeries[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const { from, to, now } = telemetryWindow()

    const res = await api.request(telemetryQuerySpec, {
      body: {
        queries: {
          twoDays: {
            period: [TELEMETRY_PERIOD_SEC],
            startedAtFrom: from.toISOString(),
            startedAtTo: to.toISOString(),
          },
        },
      },
    })

    const rows = Object.values(res.data?.results ?? {})
      .flat() as TelemetryRow[]

    setSeries(rowsToTelemetrySeries(rows, from, to, now))
    setLoading(false)
  }, [api])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useInterval(refresh, TELEMETRY_REFRESH_MS)

  return (
    <div className='TelemetryPanel Cutout Wrapped'>
      <h2>Telemetry (48 hours)</h2>

      {loading && series.length === 0 && (
        <Spinner />
      )}

      {!loading && series.length === 0 && (
        <p className='Empty'>No telemetry data yet.</p>
      )}

      {series.length > 0 && (
        <div className='MetricGrid'>
          {series.map(item => (
            <div key={item.key} className='MetricCard'>
              <div className='Label'>{item.label}</div>
              <div className='ValueRow'>
                <span className='Value'>{formatDecimal(Math.round(item.hourlyAverage))}</span>
                <span className='Unit'>/hour</span>
              </div>
              <TelemetrySparkline
                values={item.buckets}
                bucketFrom={item.bucketFrom}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
